/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '..';


export const getFiles = (feature: BaseFeature): string[] => {
    return [
        'st7789_base.py',
        'st7789_ext.py',
    ];
};

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks = [];

    const NAME = feature.getSettingValue('NAME');
    const width = feature.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
    const height = feature.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
    const SDA = feature.getSettingValue('SDA');
    const SCL = feature.getSettingValue('SCL');
    const BACKLIGHT = feature.getSettingValue('BACKLIGHT');
    const CS = feature.getSettingValue('CS');
    const RST = feature.getSettingValue('RST');
    const DC = feature.getSettingValue('DC');
    const SCK = feature.getSettingValue('SCLK');
    const MOSI = feature.getSettingValue('MOSI');
    const MISO = feature.getSettingValue('MISO');

    const INVERSION = feature.getSettingValue('INVERSION') === '1' ? 'True' : 'False';
    const MIRRORY = feature.getSettingValue('MIRRORY') === '1' ? 'True' : 'False';
    const MIRRORX = feature.getSettingValue('MIRRORX') === '1' ? 'True' : 'False';
    const XSTART = feature.getSettingValue('XSTART');
    const YSTART = feature.getSettingValue('YSTART');

    const SCREENS = feature.getSettingValue('SCREENS');

    const boardType = PlatformUtils.getPlaformWokwiId(feature.project.device.id);

    const SDAPIN = Peripherals.getGPIOPin(SDA, feature.project.device.id);
    const SCLPIN = Peripherals.getGPIOPin(SCL, feature.project.device.id);
    const CSPIN = Peripherals.getGPIOPin(CS, feature.project.device.id);
    const RSTPIN = Peripherals.getGPIOPin(RST, feature.project.device.id);
    const DCPIN = Peripherals.getGPIOPin(DC, feature.project.device.id);
    const SCKPIN = Peripherals.getGPIOPin(SCK, feature.project.device.id);
    const MOSIPIN = Peripherals.getGPIOPin(MOSI, feature.project.device.id) || 'None';
    const MISOPIN = Peripherals.getGPIOPin(MISO, feature.project.device.id) || 'None';
    const BACKLIGHTPIN = Peripherals.getGPIOPin(BACKLIGHT, feature.project.device.id);


    blocks.push({
        location: 'IMPORTS',
        blockType: BlockType.CODE,
        id: 'import st7789_base, st7789_ext',
        template: 'import st7789_base, st7789_ext',
    });

    const i2cID = '1';

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `${NAME} = st7789_ext.ST7789(
    machine.SPI(1, baudrate=10000_000, phase=0, polarity=1, sck=machine.Pin(${SCKPIN}),mosi=machine.Pin(${MOSIPIN}),miso=${MISOPIN}),
    ${width}, ${height},
    reset=machine.Pin(${RSTPIN}, machine.Pin.OUT),
    dc=machine.Pin(${DCPIN}, machine.Pin.OUT),
    cs=machine.Pin(${CSPIN}, machine.Pin.OUT))

${NAME}.init(landscape=True,mirror_y=${MIRRORY},mirror_x=${MIRRORX},inversion=${INVERSION},xstart=${XSTART},ystart=${YSTART})
machine.Pin(${BACKLIGHTPIN},machine.Pin.OUT).on()`,
    });

    blocks.push({
        location: 'WOKWI_PARTS',
        blockType: BlockType.CODE,
        template: `
{
    "type": "board-st7789",
    "id": "${NAME}",
    "top": 0,
    "left": -100,
    "attrs": {  }
},`,
    });

    blocks.push({
        location: 'WOKWI_CONNECTIONS',
        blockType: BlockType.CODE,
        template: `
            ["${NAME}:SDA", "${boardType}:GP${SDAPIN}", "green", ["v0"]],
            ["${NAME}:SCL", "${boardType}:GP${SCLPIN}", "blue", ["v0"]],
            `,
    });

    const widgetsArrayName = `${NAME}_widgets`;

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `${widgetsArrayName} = []`,
    });

    for (let j = 0; j < SCREENS.length; j++) {
        const screen = SCREENS[j];
        const WIDGETS = screen.getSettingValue('WIDGETS');

        for (let i = 0; i < WIDGETS.length; i++) {
            const widget = WIDGETS[i].getWidgetObject();
            if (widget.type === 'lcd_text_widget') {
                blocks.push({
                    location: 'display_init_widgets',
                    blockType: BlockType.CODE,
                    template: `
${widgetsArrayName}.append(DisplayWidget(
    type='text',
    x=${widget.x},
    y=${widget.y},
    width=${widget.w},
    height=${widget.h},
    screen=${j},
    text='${widget.text}',
))`,
                });
            } else if (widget.type === 'lcd_image_widget') {
                const IMG_FILE = widget.img ? widget.img.split('\n')[0] : '';
                blocks.push({
                    location: 'display_init_widgets',
                    blockType: BlockType.CODE,
                    template: `
${widgetsArrayName}.append(DisplayWidget(
    type='image',
    x=${widget.x},
    y=${widget.y},
    width=${widget.w},
    height=${widget.h},
    screen=${j},
    text='${IMG_FILE}',
))`,
                });
            }
        }
    }

    blocks.push({
        location: 'VS_MICROPYTHON_UPLOADS',
        blockType: BlockType.CODE,
        template: `
"+",
"cp",
"ssd1306.py",
":ssd1306.py",
            `,
    });

    blocks.push({
        location: 'display_display_widgets',
        blockType: BlockType.CODE,
        template: `
# ${NAME}.fill(${NAME}.color(0,0,0))
for widget in ${widgetsArrayName}:
    if widget.type == 'text':
        ${NAME}.text(widget.x,widget.y,widget.text,${NAME}.color(255,255,255),${NAME}.color(0,0,0))
        pass
    elif widget.type == 'image':
        ${NAME}.image(widget.x,widget.y,widget.text)
        pass`,
    });

    return blocks;
};
